import { type ToggleButtonGroupProps } from '@mui/material';
import {
  HiddenLegend,
  IconButton,
  SegmentedControl,
  ToggleButton as FplToggleButton,
  ToolbarPrimitive,
} from '@figma/fpl-components';
import { color } from '@figma/fpl-tokens/tokens.stylex';
import * as stylex from '@stylexjs/stylex';
import { isValidElement, type ReactNode } from 'react';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { Box } from '@/UI/Box/Box';
import { Tooltip, type TooltipProps } from '@/UI/Tooltip/Tooltip';
import { AppToggleButton, AppToggleButtonMode } from './AppToggleButton';
import { AppToggleButtonGroup } from './AppToggleButtonGroup';

export interface AppToggleButtonsOption<T extends string | number> {
  'aria-label'?: string;
  disabled?: boolean;
  id?: string;
  label: ReactNode;
  tooltipText?: ReactNode;
  value: T;
  /**
   * FPL-only: menu caret (or similar) beside this option's `IconButton` — e.g. painter shapes.
   * Switches the exclusive icon path from `SegmentedControl` to grouped `IconButton`s.
   */
  endAction?: ReactNode;
  /** FPL-only: start a bordered toolbar segment before this option (e.g. paint tools after canvas). */
  dividerBefore?: boolean;
}

export interface AppToggleButtonsProps<T extends string | number> extends Omit<
  ToggleButtonGroupProps,
  'value' | 'onChange' | 'exclusive' | 'aria-label'
> {
  /** The accessible name for the toggle group. */
  'aria-label': string;
  /**
   * The selected value
   */
  value: T | null;
  options: Array<AppToggleButtonsOption<T>>;
  /**
   * Called when the value changes
   */
  onChange: (value: T | null) => void;
  /**
   * Visual mode for the toggle buttons. 'dark', 'light', 'contained', or 'group'.
   */
  mode?: AppToggleButtonMode;
  isFigmaTheme?: boolean;
  btnW?: number;
  btnH?: number;
  isIcons?: boolean;
  svgSize?: string | number;
  gap?: number;
  tooltipPlacement?: TooltipProps['placement'];
  tooltipSlotProps?: TooltipProps['slotProps'];
  allowDeselect?: boolean;
}

const optionKey = (value: string | number) => `${typeof value}:${String(value)}`;

const getIconButtonSize = (btnH?: number): 'md' | 'lg' => (btnH != null && btnH >= 32 ? 'lg' : 'md');

const splitOptionsByDivider = <T extends string | number>(
  options: AppToggleButtonsOption<T>[],
): AppToggleButtonsOption<T>[][] => {
  const segments: AppToggleButtonsOption<T>[][] = [];
  let current: AppToggleButtonsOption<T>[] = [];
  for (const option of options) {
    if (option.dividerBefore && current.length > 0) {
      segments.push(current);
      current = [];
    }
    current.push(option);
  }
  if (current.length > 0) {
    segments.push(current);
  }
  return segments;
};

/** FPL paints the icon over the hidden radio. Keep the glyph from stealing clicks/hover. */
const FPL_OPTION_HIT_SX = {
  display: 'inline-flex',
  '& [data-fpl-component="primitive"] > span': { pointerEvents: 'none' },
};

/** FPL's track is `inline-flex` + `width: 100%`. Hug the labels instead of stretching. */
const FPL_SEGMENTED_CONTROL_HUG_SX = {
  '& fieldset [data-radio-options-root]': {
    display: 'flex !important',
    width: 'fit-content !important',
  },
  '& fieldset [data-radio-options-root] > *': {
    flex: 'none !important',
  },
};

export const AppToggleButtons = <T extends string | number>({
  value,
  options,
  onChange,
  mode = 'dark',
  isFigmaTheme,
  btnW,
  btnH,
  isIcons = false,
  svgSize,
  gap = 0.25,
  tooltipPlacement = 'right',
  tooltipSlotProps,
  allowDeselect = false,
  ...props
}: AppToggleButtonsProps<T>) => {
  const isFplMigrationEnabled = useFplMigrationFlag();
  const handleChange = (_event: React.MouseEvent<HTMLElement>, newValue: T | null) => {
    if (allowDeselect) {
      onChange(newValue);
    } else if (newValue !== null) {
      onChange(newValue);
    }
  };

  if (isFplMigrationEnabled) {
    const selectedKey = value == null ? undefined : optionKey(value);

    if (allowDeselect && isIcons) {
      return (
        <ToolbarPrimitive aria-label={props['aria-label']} orientation={props.orientation}>
          {options.map((option) => {
            const key = optionKey(option.value);
            const button = (
              <FplToggleButton
                key={key}
                checked={selectedKey === key}
                disabled={props.disabled || option.disabled}
                aria-label={option['aria-label'] ?? String(option.value)}
                onIcon={option.label}
                offIcon={option.label}
                onChange={(checked) => onChange(checked ? option.value : null)}
              />
            );
            if (!option.tooltipText) return button;
            return (
              <Tooltip
                key={key}
                title={option.tooltipText}
                placement={tooltipPlacement}
                disableInteractive
                slotProps={tooltipSlotProps}
              >
                <Box component="span" sx={{ display: 'inline-flex' }}>
                  {button}
                </Box>
              </Tooltip>
            );
          })}
        </ToolbarPrimitive>
      );
    }

    if (props.orientation !== 'vertical' && !allowDeselect) {
      const hasEndAction = options.some((option) => option.endAction != null);
      const findValue = (key: string) => options.find((option) => optionKey(option.value) === key)?.value;
      const labelsAreSupported = !isIcons || options.every((option) => isValidElement(option.label));

      // Icon toolbar with optional split trigger (e.g. painter shape menu). Canvas sits in its
      // own segment; paint tools share a left-bordered group per FPL painter spec.
      if (isIcons && hasEndAction && labelsAreSupported) {
        const size = getIconButtonSize(btnH);
        const segments = splitOptionsByDivider(options);
        return (
          <ToolbarPrimitive
            {...stylex.props(styles.iconToolbar)}
            aria-label={props['aria-label']}
            orientation={props.orientation}
          >
            {segments.map((segment) => {
              const bordered = segment[0]?.dividerBefore === true;
              return (
                <div
                  key={segment.map((option) => optionKey(option.value)).join('-')}
                  {...stylex.props(
                    styles.toolbarSegment,
                    bordered ? styles.toolbarSegmentBordered : styles.toolbarSegmentCanvas,
                  )}
                >
                  {segment.map((option) => {
                    const key = optionKey(option.value);
                    const selected = selectedKey === key;
                    const ariaLabel = option['aria-label'] ?? String(option.value);
                    const toolButton = (
                      <IconButton
                        aria-label={ariaLabel}
                        aria-pressed={selected}
                        data-tooltip={typeof option.tooltipText === 'string' ? option.tooltipText : ariaLabel}
                        data-tooltip-type="text"
                        disabled={props.disabled || option.disabled}
                        onClick={() => onChange(option.value)}
                        size={size}
                        variant={selected ? 'primary' : 'ghost'}
                      >
                        {option.label}
                      </IconButton>
                    );
                    const toolControl = option.tooltipText ? (
                      <Tooltip
                        title={option.tooltipText}
                        placement={tooltipPlacement}
                        disableInteractive
                        slotProps={tooltipSlotProps}
                      >
                        <Box component="span" sx={{ display: 'inline-flex' }}>
                          {toolButton}
                        </Box>
                      </Tooltip>
                    ) : (
                      toolButton
                    );
                    if (option.endAction != null) {
                      return (
                        <div
                          key={key}
                          {...stylex.props(styles.shapeSplit)}
                          style={{ height: btnH ?? 24 }}
                          aria-label={ariaLabel}
                          role="group"
                        >
                          {toolControl}
                          {option.endAction}
                        </div>
                      );
                    }
                    return (
                      <Box key={key} component="span" sx={{ display: 'inline-flex' }}>
                        {toolControl}
                      </Box>
                    );
                  })}
                </div>
              );
            })}
          </ToolbarPrimitive>
        );
      }

      if (labelsAreSupported) {
        return (
          <Box sx={FPL_SEGMENTED_CONTROL_HUG_SX}>
            <SegmentedControl.Root
              legend={<HiddenLegend>{props['aria-label']}</HiddenLegend>}
              value={selectedKey}
              readonly={props.disabled}
              onChange={(key) => {
                const nextValue = findValue(key);
                if (nextValue !== undefined) onChange(nextValue);
              }}
            >
              {options.map((option) => {
                const key = optionKey(option.value);
                const ariaLabel = option['aria-label'] ?? String(option.value);
                const trigger = (
                  <Box key={key} component="span" sx={FPL_OPTION_HIT_SX}>
                    {isIcons ? (
                      <SegmentedControl.Option
                        value={key}
                        icon={isValidElement(option.label) ? option.label : <></>}
                        aria-label={ariaLabel}
                        readonly={option.disabled ? true : undefined}
                      />
                    ) : (
                      <SegmentedControl.TextOption
                        value={key}
                        label={typeof option.label === 'string' ? option.label : ariaLabel}
                        readonly={option.disabled ? true : undefined}
                      />
                    )}
                  </Box>
                );
                return option.tooltipText ? (
                  <Tooltip
                    key={key}
                    title={option.tooltipText}
                    placement={tooltipPlacement}
                    disableInteractive
                    slotProps={tooltipSlotProps}
                  >
                    {trigger}
                  </Tooltip>
                ) : (
                  trigger
                );
              })}
            </SegmentedControl.Root>
          </Box>
        );
      }
    }
  }

  return (
    <AppToggleButtonGroup
      value={value}
      exclusive
      onChange={handleChange}
      sx={{ gap: mode === 'group' ? 0 : gap }}
      mode={mode}
      {...props}
    >
      {options.map((option) => {
        const button = (
          <AppToggleButton
            key={option.value}
            value={option.value}
            aria-label={option['aria-label'] || String(option.value)}
            mode={mode}
            isFigmaTheme={isFigmaTheme}
            btnW={btnW}
            btnH={btnH}
            isIcon={isIcons}
            svgSize={svgSize}
            id={option.id}
            disabled={option.disabled}
          >
            {option.label}
          </AppToggleButton>
        );

        return option.tooltipText ? (
          <Tooltip
            key={option.value}
            title={option.tooltipText}
            placement={tooltipPlacement}
            enterDelay={800}
            enterNextDelay={800}
            slotProps={tooltipSlotProps}
          >
            {button}
          </Tooltip>
        ) : (
          button
        );
      })}
    </AppToggleButtonGroup>
  );
};

const styles = stylex.create({
  iconToolbar: {
    alignItems: 'center',
    display: 'flex',
  },
  toolbarSegment: {
    alignItems: 'center',
    display: 'flex',
  },
  toolbarSegmentCanvas: {
    paddingInline: 2,
  },
  toolbarSegmentBordered: {
    borderLeftColor: color.border,
    borderLeftStyle: 'solid',
    borderLeftWidth: 1,
    gap: 2,
    paddingInline: 2,
  },
  shapeSplit: {
    alignItems: 'center',
    display: 'inline-flex',
    gap: 1,
  },
});
